import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDeliveryApi } from 'infrastructure/api';
import { FormFieldsData } from 'framework/form';
import type { DocumentStorageItemType } from 'framework/documentStorage';
import type { DocumentStorageContextData } from 'framework/documentStorage';
import { ListResponse, LoadingParams } from 'framework/list';
import { ACCEPTANCE_DOCUMENT_FILE_TYPE } from './constants';
import { normalizeStoragePath, normalizeStoragePathWithSlash } from 'domain/shared/documentStorage';
import { getFileTypeFromName } from 'domain/shared/artifact';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useProjectDataContext } from 'domain/shared/newProject';
import { acceptanceArtifactUploadValidation, editArtifactValidation } from './acceptanceValidation';
import type { AcceptanceDocumentCommitPayload } from './acceptanceTypes';

const EMPTY_LIST_RESPONSE: ListResponse<DocumentStorageItemType> = {
  result: [],
  pageNumber: 0,
  totalPages: 0,
  totalElements: 0,
};

const SEARCH_FILTER = { fileType: { eq: ACCEPTANCE_DOCUMENT_FILE_TYPE } };

const toCommitPayload = (payload: AcceptanceDocumentCommitPayload) => ({
  ...payload,
  paths: payload.paths.map(normalizeStoragePathWithSlash),
  ...(payload.previousPath && { previousPath: normalizeStoragePathWithSlash(payload.previousPath) }),
});

const toEditPayload = (projectId: string, previousPath: string, data: FormFieldsData, employeeAlias: string) => {
  const artifactIds = (data?.artifactId as string[]) ?? [];
  const newPath = artifactIds[0];
  const normalizedNew = newPath ? normalizeStoragePathWithSlash(newPath) : '';
  const normalizedPrev = previousPath ? normalizeStoragePathWithSlash(previousPath) : '';
  const isReplace = normalizedNew && normalizedPrev && normalizedNew !== normalizedPrev;

  return {
    projectId,
    paths: normalizedNew ? [normalizedNew] : [],
    fileType: ACCEPTANCE_DOCUMENT_FILE_TYPE,
    ...(isReplace && { previousPath: normalizedPrev }),
    metadata: {
      label: (data?.label as string) ?? '',
      updatedBy: employeeAlias,
      ...(newPath && { type: getFileTypeFromName(newPath) }),
    },
  };
};

const toListResponse = (result: DocumentStorageItemType[]): ListResponse<DocumentStorageItemType> => ({
  ...EMPTY_LIST_RESPONSE,
  result,
  totalElements: result.length,
});

const getArtifactFromStore = (store: Record<string, { filePreview?: string }>, path: string) =>
  store[path] ?? store[normalizeStoragePath(path)] ?? store[normalizeStoragePathWithSlash(path)];

const mapPathsToDocumentItems = (
  paths: string[],
  artifactsStore: Record<string, { filePreview?: string }>,
): DocumentStorageItemType[] =>
  paths.map(path => {
    const fileName = path.split('/').pop() ?? path;
    const fromStore = getArtifactFromStore(artifactsStore, path);
    return {
      id: path,
      path,
      type: getFileTypeFromName(fileName),
      metadata: { fileName: path, entityName: '', entityId: '', createdAt: '', createdBy: '' },
      ...(fromStore?.filePreview && { filePreview: fromStore.filePreview }),
    } as DocumentStorageItemType & { filePreview?: string };
  });

const useAcceptanceDocumentsService = () => {
  const { sendPostRequest } = useDeliveryApi();
  const { getUserAlias } = useAuthenticatedUserService();
  const { data: projectData } = useProjectDataContext();
  const artifacts = useSelector(
    (state: { artifacts?: Record<string, { filePreview?: string }> }) => state.artifacts ?? {},
  );

  const searchDocuments = useCallback(
    async (params: LoadingParams): Promise<ListResponse<DocumentStorageItemType>> => {
      const projectId = (params?.filter as Record<string, { eq?: string }>)?.['projectId']?.eq;
      if (!projectId) return EMPTY_LIST_RESPONSE;
      try {
        const res = await sendPostRequest(`/projects/documents/${projectId}/search`, { filter: SEARCH_FILTER });
        const data = await res.json();
        const result = (data?.result ?? []) as DocumentStorageItemType[];
        return toListResponse(result);
      } catch {
        return EMPTY_LIST_RESPONSE;
      }
    },
    [sendPostRequest],
  );

  const uploadDocument = useCallback(
    async (data: FormFieldsData | AcceptanceDocumentCommitPayload) => {
      const payload = data as AcceptanceDocumentCommitPayload;
      const res = await sendPostRequest('/projects/documents/commit', toCommitPayload(payload));
      const result = (await res.json()) as { paths?: string[]; id?: string[] };
      const paths = payload.paths ?? [];
      return { paths: result?.paths ?? paths, id: result?.id ?? paths };
    },
    [sendPostRequest],
  );

  const deleteDocument = useCallback(
    async (path: string): Promise<void> => {
      const res = await sendPostRequest('/projects/documents/delete', { path: normalizeStoragePath(path) });
      if (!res.ok) throw new Error('Failed to delete document');
    },
    [sendPostRequest],
  );

  const getDocumentToken = useCallback(
    async (path: string): Promise<string> => {
      if (!path) return '';
      try {
        const res = await sendPostRequest('/projects/documents/presigned-link', {
          path: normalizeStoragePath(path),
        });
        if (!res.ok) return '';
        const data = await res.json();
        return data?.metadata?.preSignedUrl ?? data?.preSignedUrl ?? data?.url ?? '';
      } catch {
        return '';
      }
    },
    [sendPostRequest],
  );

  const editDocument = useCallback(
    async (data: FormFieldsData) => {
      const path = (data as Record<string, unknown>)?.currentPath as string | undefined;
      const artifactIds = (data?.artifactId as string[]) ?? [];
      const projectId = projectData?.id;

      if (!path) return data;
      if (artifactIds.length === 0) {
        await deleteDocument(path);
        return data;
      }
      if (!projectId) return data;

      const employeeAlias = (await getUserAlias()) ?? '';
      const body = toEditPayload(projectId, path, data, employeeAlias);
      const res = await sendPostRequest('/projects/documents/commit', body);
      if (!res.ok) throw new Error('Failed to update document metadata');
      await res.json();
      return data;
    },
    [sendPostRequest, projectData?.id, getUserAlias, deleteDocument],
  );

  const getDocumentByPaths = useCallback(
    (paths: string[]) => Promise.resolve(mapPathsToDocumentItems(paths, artifacts)),
    [artifacts],
  );

  const getValidationSchema = useCallback(
    () => Promise.resolve({ ...editArtifactValidation, ...acceptanceArtifactUploadValidation }),
    [],
  );

  return useMemo<DocumentStorageContextData>(
    () => ({
      searchDocuments,
      getDocumentByPaths,
      uploadDocument,
      editDocument,
      deleteDocument,
      getDocumentToken,
      getValidationSchema,
    }),
    [
      searchDocuments,
      getDocumentByPaths,
      uploadDocument,
      editDocument,
      deleteDocument,
      getDocumentToken,
      getValidationSchema,
    ],
  );
};

export default useAcceptanceDocumentsService;
